/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  AgGridType,
  IonGrid,
  IonGridAPI,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {dlsLayout} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import FileHistoryStatusCell from './FileHistoryStatusCell';
import {
  FileTypesFields,
  getCurrentLastUpdateTime,
  getCurrentNewVersion,
  getJobId,
  getMDTCurrentVersion,
  getMDTJobId,
  getMDTPendingFileVersion,
  getMDTPendingJobId,
  getMDTPendingJobStatus,
  getPendingJobStatus,
  getPlatform,
  getVersion,
  JobStatusType
} from './ViewDeviceEditorUtils';

export interface FileHistoryGridProps {
  rowData?: Array<any>;
  loading?: boolean;
  isLogsView?: boolean;
  selectedJobId?: (jobId: number) => void;
  isToggleSwitchGrid?: boolean;
  setApi?: Function;
};

// defines the styles used by WidgetGrid component
export const useStyles = makeStyles((theme: DLSTheme) => ({
  loader : {
    height         : '100%',
    minHeight      : '200px',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  filesGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      height                                 : '100%',
      '& .ag-body-viewport.ag-layout-normal' : {
        overflowY : 'auto'
      },
      '& .ag-overlay-no-rows-center' : {
        marginTop : 0
      }
    }
  }
}));

/**
 * Grid to be rendered on the File Tab under Device Details page
 */
export const FileHistoryGrid = ({
  setApi,
  selectedJobId,
  rowData = [],
  loading = false,
  isLogsView = false,
  isToggleSwitchGrid = false
}: FileHistoryGridProps, ref) => {
  const classes                                         = useStyles();
  const ionGridRef                                      = useRef<IonGridAPI>();
  const isCurrentViewRef                                = useRef<boolean>(!isLogsView);
  const [gridApi, setGridApi]                           = useState<AgGridType>(null);
  const [hasItems, setHasItems]                         = useState<boolean>(false);
  const [refreshRequired, setRefreshRequired]           = useState(false);
  const [logsViewDefinitions, setLogsViewDefinitions]   = useState(getColumnDefinitions(isLogsView));
  const [hasMultiFileTypes, setHasMultiFileTypes]       = useState<boolean>(false);
  const [rowMultiFileTypeData, setRowMultiFileTypeData] = useState<string[]>([]);
  const gridRowDataLength                               = (hasMultiFileTypes && !isLogsView)
    ? rowMultiFileTypeData?.length
    : rowData?.length;

  useEffect(() => {
    setLogsViewDefinitions(getColumnDefinitions(isLogsView));
    isCurrentViewRef.current = !isLogsView;
  }, [isLogsView]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : logsViewDefinitions,
    columnState       : null
  });

  useEffect(() => {
    if (gridApi) {
      setApi(gridApi);
    }
  }, [gridApi]);

  useEffect(() => {
    gridApi?.setGridOption('columnDefs', defaultDefinitions);
  }, [logsViewDefinitions]);

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(isLogsView ? defaultDefinitions : definitions);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser
  }));

  const onColumnSelectionsUpdate = useCallback((columns: ColDef[]): void => {
    onColumnChooserSave(columns);
  }, []);

  const getFileType = useCallback((files: GenericObject[], fileType: string) => {
    return files?.some(({platforms}) => platforms?.value?.includes(fileType));
  }, []);

  /**
   * Takes a list of file history and constructs a list that
   * can be easily consumed by the IonGrid API
   */
  const constructFileHistoryGridRows = useCallback(function(files: GenericObject[]): GridResult[] {
    let gridItems;
    const hasAndroidMDT    = getFileType(files, FileTypesFields.ANDROIDMDT);
    const hasLinuxOPKG     = getFileType(files, FileTypesFields.LINUXOPKG);
    const hasLMUConfig     = getFileType(files, FileTypesFields.LMUCONFIG);
    const hasMultiFileType = hasAndroidMDT || hasLinuxOPKG || hasLMUConfig;
    if (hasMultiFileType && !isLogsView) {
      setHasMultiFileTypes(hasMultiFileType);
      files?.forEach(item => {
        const matchingPlatforms = item?.current?.concat(item?.pending)?.filter(data => data?.id === item?.platform?.id);
        matchingPlatforms?.forEach(result => {
          const {
            platforms,
            current,
            pending
          } = item || {};
          result.platforms = platforms;
          if (result.jobStatus === JobStatusType.COMPLETED || result.transactionStatus === JobStatusType.SUCCESS) {
            result.currentLength = current?.length;
          }
          if (result.jobStatus === JobStatusType.IN_PROGRESS) {
            result.pendingLength = pending?.length;
          }
        });
      });
      const updatedData = files?.flatMap(({current, pending}) => [...current, ...pending]);
      setRowMultiFileTypeData(updatedData);
      gridItems = updatedData?.map(data => ({
        ...data,
        fileTypeUI              : getPlatform(data),
        jobIdUI                 : getMDTJobId(data),
        currentLastUpdateTimeUI : formatDateTime(data?.lastUpdateTime),
        currentNewVersion       : getMDTCurrentVersion(data),
        nameUI                  : data?.reportedName,
        pendingJobIdUI          : getMDTPendingJobId(data),
        pendingJobStatusUI      : getMDTPendingJobStatus(data),
        pendingFileVersionUI    : getMDTPendingFileVersion(data)
      })) as GridResult[];
    } else {
      setHasMultiFileTypes(false);
      gridItems = files.map(file => ({
        ...file,
        fileTypeUI              : getPlatform(file),
        jobIdUI                 : getJobId(file),
        requestTimeUI           : formatDateTime(file?.requestTime),
        lastUpdateTimeUI        : formatDateTime(file?.lastUpdateTime),
        oldVersionUI            : getVersion(file),
        newVersionUI            : getVersion(file, false),
        statusUI                : file?.transactionStatus?.name,
        nameUI                  : file?.newFileName,
        currentLastUpdateTimeUI : getCurrentLastUpdateTime(file),
        currentNewVersion       : getCurrentNewVersion(file),
        pendingJobIdUI          : file?.pending?.[0]?.jobId,
        pendingJobStatusUI      : getPendingJobStatus(file),
        pendingFileVersionUI    : file?.pending?.[0]?.newVersion
      })) as GridResult[];
    }
    setHasItems(!!gridItems.length);
    return gridItems;
  }, [isLogsView]);

  const getGridHeight = useCallback(function() {
    return classes.filesGrid;
  }, []);

  useEffect(() => {
    if (loading) {
      gridApi?.paginationGoToFirstPage();
    }
  }, [loading]);

  useEffect(() => {
    setRefreshRequired(loading);
  }, [rowData, loading]);

  const onColumnChooserClose = useCallback(function(): void {
    toggleColumnChooser(false);
  }, []);

  const onJobLinkClicked = useCallback(({data}: GenericObject) => {
    const {jobIdUI} = data || {};
    selectedJobId(jobIdUI);
  }, [selectedJobId]);

  const onPendingJobLinkClicked = useCallback(({data}: GenericObject) => {
    if (data?.pending) {
      selectedJobId(data?.pending?.[0]?.jobId);
    } else {
      selectedJobId(data?.command?.job?.jobId || data?.commandResult?.properties?.job?.jobId || data?.jobId);
    }
  }, [selectedJobId]);

  return (
    <IonGrid omitHeader
      ref={ionGridRef}
      rowModelType={RowModelTypes.ClientSide}
      rowData={rowData}
      totalRows={gridRowDataLength}
      isLoading={loading}
      augmentItems={constructFileHistoryGridRows}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      isToggleSwitchGrid={isToggleSwitchGrid}
      columnComponents={{
        jobIdCell : (item: any) => (
          <LinkCell data={item}
            onClick={onJobLinkClicked} />
        ),
        statusCell : (item: any) => (
          <FileHistoryStatusCell data={item?.data}
            isCurrentView={isCurrentViewRef?.current} />
        ),
        pendingJobIdCell : (item: any) => (
          <LinkCell data={item}
            onClick={onPendingJobLinkClicked} />
        )
      }}
      onColumnChooserClose={onColumnChooserClose}
      refreshRequired={refreshRequired}
      setRefreshRequired={setRefreshRequired}
      checkboxesEnabled={false}
      showHeaderCheckbox={false}
      viewSubGroups={false}
      rowMenusEnabled={false}
      rowHeight={40}
      onGridReady={setGridApi}
      getGridHeight={getGridHeight}
      paginationEnabled
      showCustomPaginationBar
      pageOffset
      pageSizes={PAGE_SIZES} />
  );
};

/**
 * Returns column definitions for desktop file history grid
 */
const getColumnDefinitionsDesktop = (isLogsView): ColDef[] => {
  if (isLogsView) {
    return [{
      headerName : translate('admin-devices:view.files.grid.filetype'),
      field      : 'fileTypeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.jobId'),
      field        : 'jobIdUI',
      cellRenderer : 'jobIdCell',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.jobCreated'),
      field      : 'requestTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.lastUpdate'),
      field      : 'lastUpdateTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.oldVersion'),
      field        : 'oldVersionUI',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'oldVersionUI'
    },
    {
      headerName   : translate('admin-devices:view.files.grid.newVersion'),
      field        : 'newVersionUI',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'newVersionUI'
    },
    {
      headerName         : translate('admin-devices:view.files.grid.status'),
      field              : 'statusUI',
      cellRenderer       : 'statusCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    }
    ];
  } else {
    return [{
      headerName : translate('admin-devices:view.files.grid.filetype'),
      field      : 'fileTypeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.currentJobId'),
      field        : 'jobIdUI',
      cellRenderer : 'jobIdCell',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.lastUpdate'),
      field      : 'currentLastUpdateTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.currentFileVersion'),
      field        : 'currentNewVersion',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'newVersionUI'
    },
    {
      headerName : translate('admin-devices:view.files.grid.fileName'),
      field      : 'nameUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName         : translate('admin-devices:view.files.grid.pendingJobId'),
      field              : 'pendingJobIdUI',
      cellRenderer       : 'pendingJobIdCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.pendingFileVersion'),
      field      : 'pendingFileVersionUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName         : translate('admin-devices:view.files.grid.pendingJobStatus'),
      field              : 'pendingJobStatusUI',
      cellRenderer       : 'statusCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    }];
  }
};

/**
 * Returns column definitions for mobile file history grid
 */
const getColumnDefinitionsMobile = (isLogsView): ColDef[] => {
  if (isLogsView) {
    return [{
      headerName : translate('admin-devices:view.files.grid.filetype'),
      field      : 'fileTypeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.jobId'),
      field        : 'jobIdUI',
      cellRenderer : 'jobIdCell',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.jobCreated'),
      field      : 'requestTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.lastUpdate'),
      field      : 'lastUpdateTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.oldVersion'),
      field        : 'oldVersionUI',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'oldVersionUI'
    },
    {
      headerName   : translate('admin-devices:view.files.grid.newVersion'),
      field        : 'newVersionUI',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'newVersionUI'
    },
    {
      headerName         : translate('admin-devices:view.files.grid.status'),
      field              : 'statusUI',
      cellRenderer       : 'statusCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    }];
  } else {
    return [{
      headerName : translate('admin-devices:view.files.grid.filetype'),
      field      : 'fileTypeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.currentJobId'),
      field        : 'jobIdUI',
      cellRenderer : 'jobIdCell',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.lastUpdate'),
      field      : 'currentLastUpdateTimeUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName   : translate('admin-devices:view.files.grid.currentFileVersion'),
      field        : 'currentNewVersion',
      sortable     : false,
      filter       : false,
      resizable    : true,
      hide         : false,
      tooltipField : 'newVersionUI'
    },
    {
      headerName : translate('admin-devices:view.files.grid.fileName'),
      field      : 'nameUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName         : translate('admin-devices:view.files.grid.pendingJobId'),
      field              : 'pendingJobIdUI',
      cellRenderer       : 'pendingJobIdCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    },
    {
      headerName : translate('admin-devices:view.files.grid.pendingFileVersion'),
      field      : 'pendingFileVersionUI',
      sortable   : false,
      filter     : false,
      resizable  : true,
      hide       : false
    },
    {
      headerName         : translate('admin-devices:view.files.grid.pendingJobStatus'),
      field              : 'pendingJobStatusUI',
      cellRenderer       : 'statusCell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      hide               : false
    }];
  }
};
const getColumnDefinitions = (isLogsView) => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop(isLogsView)),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile(isLogsView))
});
export default forwardRef(FileHistoryGrid);
